import { StudentCaseManagerInterface, StudentCaseManagerStateInterface } from '@/types/Student';

import { AxiosResponse } from 'axios';
import { StudentService } from '@/services';
import { defineStore } from 'pinia';

export default defineStore('StudentCaseManagerStore', {
  state: (): StudentCaseManagerStateInterface => ({
    caseManagersForStudentSelected: [],
    currentStudentId: undefined,
    error: '',
    loading: false,
  }),

  getters: {},

  actions: {
    catchError(e: Error) {
      this.loading = false;
      this.error = 'error';
      throw e;
    },
    loaded() {
      this.loading = false;
      this.error = undefined;
    },

    /**
     * Assign case managers to a specific student
     */
    async attachCaseManagers(payload: { caseManagers: number[]; studentId: number }): Promise<AxiosResponse<unknown>> {
      const { studentId, caseManagers } = payload;
      const response = await StudentService.attachCaseManagersRequest(caseManagers, studentId).catch(this.catchError);

      this.loaded();
      return response;
    },

    /**
     * Remove case managers from a specific student
     */
    async detachCaseManagers(payload: { caseManagers: number[]; studentId: number }): Promise<AxiosResponse<unknown>> {
      const { studentId, caseManagers } = payload;
      const response = await StudentService.detachCaseManagersRequest(caseManagers, studentId).catch(this.catchError);

      this.loaded();
      return response;
    },

    /**
     * getCaseManagersForStudent
     *
     * Retrieve all case managers assigned to a student
     */
    async getCaseManagersForStudent(studentId: number): Promise<StudentCaseManagerInterface[]> {
      this.loading = true;
      this.currentStudentId = studentId;
      const response = await StudentService.getCaseManagersForStudentRequest(studentId).catch(this.catchError);

      // Ignore a response for a student the caller has since navigated away from —
      // otherwise a slower earlier request can overwrite the currently-viewed student's list.
      if (this.currentStudentId !== studentId) return this.caseManagersForStudentSelected || [];

      this.caseManagersForStudentSelected = response.data?.data as StudentCaseManagerInterface[];

      this.loaded();
      return this.caseManagersForStudentSelected;
    },

    /**
     * getAddCaseManagersModalData
     *
     * Retrieve all case managers available to attach to a student
     */
    async getAddCaseManagersModalData(studentId: number): Promise<{ name: string; value: number }[]> {
      const request = await StudentService.getAddCaseManagersModalData(studentId).catch(this.catchError);
      const availableToAdd: StudentCaseManagerInterface[] = request.data?.data;

      return availableToAdd.map((caseManager) => {
        return {
          value: caseManager.id,
          name: caseManager.display_name as string,
        };
      });
    },
  },
});
